import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from '@reach/router';

import * as styles from './SearchBox.module.scss';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'gatsby';
import { sortBy } from '../utils/utils';
import { useWindowSize } from '../utils/hooks';

const MAX_SEARCH_RESULTS = 1000;
const N_CHARS_PER_TYPO = 4;
const MS_TO_WAIT_FOR_STOP_TYPING = 1000;

function normalizeString(str: string) {
  let normalizedStr = str.toLowerCase();
  if (normalizedStr.normalize) {
    normalizedStr = normalizedStr.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  return normalizedStr;
}

function getTextWithBoldedMatches(
  text: string,
  searchText: string,
  renderNamePart: (namePart: string) => React.ReactNode,
): Array<React.ReactNode> {
  return (
    normalizeString(text)
      .replace(new RegExp('(' + normalizeString(searchText) + ')', 'ig'), '|||$1|||')
      .split('|||')
      // Convert normalized entry name parts to non normalized entry name parts
      .reduce((acc: Array<string>, namePart) => {
        // sum all name parts to get accLength
        const accLength = acc.reduce((a, v) => a + v.length, 0);
        return [...acc, text.slice(accLength, accLength + namePart.length)];
      }, [])
      .map((namePart, i) =>
        normalizeString(namePart) === normalizeString(searchText) ? (
          <strong key={i}>{renderNamePart(namePart)}</strong>
        ) : (
          renderNamePart(namePart)
        ),
      )
  );
}

function getValueByType<
  T extends string,
  V extends undefined | string | number | boolean | React.ReactElement,
>(valueOrObj: V | Partial<Record<T, V>>, type: T | null): V | undefined {
  if (
    typeof valueOrObj === 'undefined' ||
    typeof valueOrObj === 'string' ||
    typeof valueOrObj === 'number' ||
    typeof valueOrObj === 'boolean' ||
    React.isValidElement(valueOrObj)
  ) {
    return valueOrObj as V;
  } else if (type !== null) {
    return (valueOrObj as Record<T, V>)[type];
  } else {
    throw new Error('Got valueOrObj as an object, but type is null');
  }
}

function getLevenshteinDistanceMatrix(str1: string, str2: string) {
  const matrix = Array(str1.length + 1);
  for (let row = 0; row <= str1.length; row++) {
    matrix[row] = Array(str2.length);
  }
  matrix[0][0] = 0;
  for (let row = 1; row <= str1.length; row++) {
    matrix[row][0] = row;
  }
  for (let col = 1; col <= str2.length; col++) {
    matrix[0][col] = col;
  }
  for (let row = 1; row <= str1.length; row++) {
    for (let col = 1; col <= str2.length; col++) {
      matrix[row][col] = Math.min(
        matrix[row - 1][col] + 1,
        matrix[row][col - 1] + 1,
        matrix[row - 1][col - 1] + (str1[row - 1] === str2[col - 1] ? 0 : 1),
      );
    }
  }

  return matrix;
}

// Tests whether the searchText matches the start of targetText assuming some typos.
// The number of typos allowed depends on the size of searchText
function areTypoMatch(searchText: string, targetText: string): boolean {
  const nTyposAllowed = Math.floor(searchText.length / N_CHARS_PER_TYPO);
  if (searchText.length - targetText.length > nTyposAllowed) {
    return false;
  }
  const targetTextPrefix = targetText.substring(
    0,
    Math.min(targetText.length, searchText.length + nTyposAllowed),
  );
  const levenshteinDistMatrix = getLevenshteinDistanceMatrix(searchText, targetTextPrefix);
  const nTypos = Math.min(...levenshteinDistMatrix[levenshteinDistMatrix.length - 1]);
  return 0 < nTypos && nTypos <= nTyposAllowed;
}

function getMatchResults<EntryType>(
  searchText: string,
  entries: Array<Entry<EntryType>>,
  matchFieldConfigs: Record<string, string> | undefined,
  matchFunc: (searchStr: string, targetStr: string) => boolean = (searchStr, targetStr) =>
    targetStr.includes(searchStr),
): Array<
  Entry<EntryType> & {
    matchReason: MatchReason;
  }
> {
  return sortBy(
    entries
      .map(entry => ({
        ...entry,
        matchReason:
          (matchFunc(normalizeString(searchText), normalizeString(entry.name)) &&
            ('name' as const)) ||
          (entry.matchFields &&
            entry.matchFields.reduce(
              (result: null | { fieldName: string; name: string; value: string }, field) => {
                if (result) {
                  return result;
                }
                const matchedValue = field.values.find(value =>
                  matchFunc(normalizeString(searchText), normalizeString(value)),
                );
                return matchedValue
                  ? {
                      fieldName: field.name,
                      name: (matchFieldConfigs && matchFieldConfigs[field.name]) || field.name,
                      value: matchedValue,
                    }
                  : null;
              },
              null,
            )) ||
          null,
      }))
      .filter(entry => entry.matchReason),
    [
      entry => (entry.matchReason === 'name' ? 0 : 1),
      entry =>
        matchFieldConfigs && entry.matchReason !== 'name' && entry.matchReason !== null
          ? Object.keys(matchFieldConfigs).indexOf(entry.matchReason.fieldName)
          : -1,
    ],
  );
}

export interface MatchField {
  name: string;
  values: Array<string>;
}

type MatchReason =
  | 'name'
  | {
      fieldName: string;
      name: string;
      value: string;
    }
  | null;

interface Entry<EntryType> {
  name: string;
  url: string;
  value?: EntryType;
  matchFields?: Array<MatchField>;
}

type SearchBoxProps<T extends string, EntryType> = {
  className?: string;
  withBorderGreen?: boolean;
  entriesGroups: Array<{
    title?: string;
    type?: T;
    entries: Array<Entry<EntryType>>;
    matchFieldConfigs?: Record<string, string>;
  }>;
  placeholder?: string | Partial<Record<T, string>>;
  placeholderLong?: string | Partial<Record<T, string>>;
  minSearchTextChars?: number | Partial<Record<T, number>>;
  withTypoCorrection?: boolean | Partial<Record<T, boolean>>;
  noResultsMessage: string | React.ReactElement | Record<T, string | React.ReactElement>;
  onResultChosen?: (value: EntryType) => void;
  renderNamePart?: (namePart: string) => React.ReactNode;
  renderResultEntry?: (entry: Entry<EntryType>, key: string | number) => React.ReactNode;
} & (
  | {
      typeOptions?: never;
      // Used only for search tracking purposes
      contentType: string;
    }
  | {
      typeOptions: {
        label: string;
        radios: Array<{
          inputValue: T;
          label: string;
        }>;
      };
      contentType?: never;
    }
);

const SearchBox = <T extends string, EntryType>({
  className,
  typeOptions,
  contentType,
  withBorderGreen = false,
  withTypoCorrection = false,
  entriesGroups,
  placeholder,
  placeholderLong,
  minSearchTextChars,
  noResultsMessage,
  onResultChosen,
  renderNamePart = namePart => namePart,
  renderResultEntry,
}: SearchBoxProps<T, EntryType>): React.ReactElement => {
  if (!typeOptions && typeof noResultsMessage === 'object') {
    throw new Error('Prop noResultsMessage must be a string if typeOptions is not passed.');
  }
  const { width } = useWindowSize();
  const location = useLocation();
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const [selectedType, setSelectedType] = useState(
    typeOptions && typeOptions.radios && typeOptions.radios[0]
      ? typeOptions.radios[0].inputValue
      : null,
  );
  const [searchText, setSearchText] = useState('');
  const searchInputEl = useRef<HTMLInputElement>(null);
  const preventNextUnfocus = useRef(false);

  const selectedTypeGroupsWithMatches = entriesGroups
    .filter(group => !selectedType || group.type === selectedType)
    .map(group => {
      const matchResults = getMatchResults(searchText, group.entries, group.matchFieldConfigs);
      const typoMatchResults = getValueByType(withTypoCorrection, selectedType)
        ? getMatchResults(searchText, group.entries, group.matchFieldConfigs, areTypoMatch).filter(
            typoMatchResult =>
              !matchResults.find(matchResult => matchResult.name === typoMatchResult.name),
          )
        : [];
      return {
        ...group,
        matchResults,
        typoMatchResults,
      };
    });

  const maxSearchResultsPerGroup = Math.max(
    Math.ceil((MAX_SEARCH_RESULTS - entriesGroups.length) / entriesGroups.length),
    1,
  );

  const searchBoxPlaceholder =
    width && width >= 768 && getValueByType(placeholderLong, selectedType)
      ? getValueByType(placeholderLong, selectedType)
      : getValueByType(placeholder, selectedType) || t('search_box.placeholder', 'Enter keyword');

  // Track search queries
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (
        searchText.length &&
        searchText.length >= (getValueByType(minSearchTextChars, selectedType) || 0)
      ) {
        if (window && window.ga) {
          window.ga(
            'send',
            'pageview',
            `/search_results?q=${encodeURIComponent(searchText)}&type=${encodeURIComponent(
              selectedType || contentType || 'null',
            )}&path=${encodeURIComponent(location.pathname)}`,
          );
        }
      }
    }, MS_TO_WAIT_FOR_STOP_TYPING);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchText, selectedType]);

  function internalRenderResultEntry(
    resultEntry: Entry<EntryType> & {
      matchReason: MatchReason;
    },
    key: string | number,
    renderPotentialMatchText: (potentialMatchText: string) => React.ReactNode,
  ) {
    return renderResultEntry ? (
      renderResultEntry(resultEntry, key)
    ) : (
      <Link
        key={key}
        className={styles.result}
        title={resultEntry.name}
        to={resultEntry.url}
        onClick={e => {
          e.stopPropagation();
          if (onResultChosen && resultEntry.value) {
            onResultChosen(resultEntry.value);
          }
        }}
      >
        <span className={styles.resultName}>{renderPotentialMatchText(resultEntry.name)}</span>
        {resultEntry.matchReason !== 'name' && (
          <>
            <span className={styles.matchLocationSeparator}>|</span>
            <span className={styles.matchLocation}>
              <span className={styles.matchLocationValue}>
                {renderPotentialMatchText(resultEntry.matchReason!.value)}{' '}
              </span>
              <span className={styles.matchLocationType}>
                <Trans
                  i18nKey="search_box.search_match_location_text"
                  defaults="in {{search_match_location_type}}"
                  values={{
                    search_match_location_type: resultEntry.matchReason!.name,
                  }}
                />
              </span>
            </span>
          </>
        )}
      </Link>
    );
  }

  return (
    <div className={styles.container + (className ? ' ' + className : '')}>
      {/* SEARCH TYPE RADIOS */}
      {!!typeOptions && (
        <div className={styles.labelContainer}>
          <span className={styles.searchDescription}>{typeOptions.label}</span>
          {typeOptions.radios &&
            typeOptions.radios.map((radio, i) => (
              <div key={i} className={styles.radioButtonContainer}>
                <label htmlFor={radio.inputValue} className={styles.radioButton}>
                  <input
                    type="radio"
                    id={radio.inputValue}
                    name="search"
                    value={radio.inputValue}
                    checked={selectedType === radio.inputValue}
                    onChange={e => {
                      setSelectedType(e.target.value as T);
                      // Do this to prevent losing focus and thus closing the search results box
                      if (searchInputEl.current) {
                        searchInputEl.current.focus();
                      }
                      preventNextUnfocus.current = true;
                    }}
                  />
                  <FontAwesomeIcon icon="check" className={styles.checkmark} />
                  <span className={styles.radioButtonLabel}>{radio.label}</span>
                </label>
              </div>
            ))}
        </div>
      )}

      {/* INPUT */}
      <div
        className={
          styles.searchBox +
          (withBorderGreen ? ' ' + styles.borderGreen : ' ') +
          ' ' +
          styles.selected
        }
      >
        <label className={styles.hiddenLabel} htmlFor="SearchBox">
          {searchBoxPlaceholder}
        </label>
        <input
          className={styles.searchInput}
          ref={searchInputEl}
          type="text"
          name=""
          id="SearchBox"
          placeholder={searchBoxPlaceholder}
          onFocus={() => setIsFocused(true)}
          // HACK Wait 200 ms before setting focused to false, to prevent
          // closing the results box before the click event on the link being registered.
          // See: https://stackoverflow.com/questions/9335325/blur-event-stops-click-event-from-working
          onBlur={() => {
            setTimeout(() => {
              if (!preventNextUnfocus.current) {
                setIsFocused(false);
              } else if (searchInputEl.current) {
                searchInputEl.current.focus();
              }
              preventNextUnfocus.current = false;
            }, 200);
          }}
          onChange={e => {
            setSearchText(e.target.value);
          }}
          value={searchText}
        />

        <div className={styles.button + (withBorderGreen ? ' ' + styles.buttonGreen : '')}>
          <FontAwesomeIcon icon="search" />
        </div>

        {/* RESULTS BOX */}
        {!!searchText && isFocused && (
          <div
            className={styles.resultsBox}
            onClick={() => {
              if (searchInputEl.current && searchInputEl.current !== document.activeElement) {
                preventNextUnfocus.current = true;
              }
            }}
            onScroll={() => {
              if (searchInputEl.current && searchInputEl.current !== document.activeElement) {
                preventNextUnfocus.current = true;
              }
            }}
          >
            {/* NOT ENOUGH CHARS */}
            {searchText.length < (getValueByType(minSearchTextChars, selectedType) || 0) ? (
              <div className={styles.noResultsMessage}>
                <FontAwesomeIcon icon="info-circle" className={styles.infoIcon} />{' '}
                {t(
                  'search_box.not_enough_chars_text',
                  'Type at least {{min_chars}} characters to see results.',
                  { min_chars: getValueByType(minSearchTextChars, selectedType) || 0 },
                )}
              </div>
            ) : (
              <>
                {selectedTypeGroupsWithMatches.every(group => group.matchResults.length === 0) ? (
                  <div className={styles.noResultsMessage}>
                    <FontAwesomeIcon icon="info-circle" className={styles.infoIcon} />{' '}
                    {typeof noResultsMessage === 'object' && !React.isValidElement(noResultsMessage)
                      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        noResultsMessage[selectedType!]
                      : noResultsMessage}
                  </div>
                ) : (
                  selectedTypeGroupsWithMatches.map(
                    (group, i) =>
                      /* MATCH RESULTS */
                      group.matchResults.length > 0 && (
                        <div
                          key={i}
                          className={
                            styles.resultsGroup +
                            (group.title ? ' ' + styles.resultsGroupTitled : '')
                          }
                        >
                          {!!group.title && <h4 className={styles.groupTitle}>{group.title}</h4>}
                          <div className={styles.groupResults}>
                            {group.matchResults
                              .slice(0, maxSearchResultsPerGroup)
                              .map((resultEntry, j) =>
                                internalRenderResultEntry(resultEntry, j, potentialMatchText =>
                                  getTextWithBoldedMatches(
                                    potentialMatchText,
                                    searchText,
                                    renderNamePart,
                                  ),
                                ),
                              )}
                            {group.matchResults.length > maxSearchResultsPerGroup && (
                              <div className={styles.moreResultsEllipsis}>...</div>
                            )}
                          </div>
                        </div>
                      ),
                  )
                )}

                {/* TYPO MATCH RESULTS */}
                {selectedTypeGroupsWithMatches.some(
                  group => group.typoMatchResults.length !== 0,
                ) && (
                  <div className={styles.similarResultsContainer}>
                    <div className={styles.similarResultsTitle}>
                      {t('search_box.similar_results_label', 'Similar results')}
                    </div>
                    <div className={styles.similarResults}>
                      {selectedTypeGroupsWithMatches
                        .filter(group => group.typoMatchResults.length)
                        .map(group =>
                          group.typoMatchResults
                            // Didn't bother with a different logic for limiting results
                            // as it's not a problem for now
                            .slice(0, maxSearchResultsPerGroup)
                            .map((resultEntry, j) =>
                              internalRenderResultEntry(resultEntry, j, potentialMatchText =>
                                renderNamePart(potentialMatchText),
                              ),
                            ),
                        )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBox;
